import { Action, Reducer } from 'redux';
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonExternalAssociations.Actions';
import { PersonExternalAssociationState, PersonExternalAssociationUnloadState } from '../states/PersonExternalAssociationState';
import { ExternalEntityAssociationViewModel } from '../../models/Entities/Person/ExternalAssociation/ExternalAssociationModels';

export type KnownAction =
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetAllRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetAllSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetAllFailureAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetByIdRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetByIdSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetByIdFailureAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationCreateRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationCreateSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationCreateFailureAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationUpdateRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationUpdateSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationUpdateFailureAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationDeleteRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationDeleteSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationDeleteFailureAction;

export const PersonExternalAssociationsReducer: Reducer<PersonExternalAssociationState> = (
    state: PersonExternalAssociationState | undefined,
    incomingAction: Action
): PersonExternalAssociationState => {
    if (state === undefined) {
        return PersonExternalAssociationUnloadState as PersonExternalAssociationState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETALL_REQUEST:
            return {
                ...state,
                isLoadingAll: true,
                successLoadingAll: undefined,
                failOnLoadingAll: false,
                error: undefined
            };
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETALL_SUCCESS: {
            const otherPersons = state.list.filter((item: ExternalEntityAssociationViewModel) => item.personId !== action.personId);
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: true,
                failOnLoadingAll: false,
                list: [...otherPersons, ...action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETALL_FAILURE:
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: false,
                failOnLoadingAll: true,
                error: action.error
            };

        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETBYID_REQUEST:
            return {
                ...state,
                isLoadingOne: true,
                successLoadingOne: undefined,
                failOnLoadingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETBYID_SUCCESS: {
            const filtered = state.list.filter((item: ExternalEntityAssociationViewModel) => item.id !== action.payload.id);
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: true,
                failOnLoadingOne: false,
                list: [...filtered, action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETBYID_FAILURE:
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: false,
                failOnLoadingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_CREATE_REQUEST:
            return {
                ...state,
                isAddingNewOne: true,
                successAddingNewOne: undefined,
                failOnAddingNewOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_CREATE_SUCCESS: {
            const createdList = state.list.some((item: ExternalEntityAssociationViewModel) => item.id === action.payload.id)
                ? state.list.map((item: ExternalEntityAssociationViewModel) => (item.id === action.payload.id ? action.payload : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: true,
                failOnAddingNewOne: false,
                lastCreated: action.payload,
                list: createdList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_CREATE_FAILURE:
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: false,
                failOnAddingNewOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_UPDATE_REQUEST:
            return {
                ...state,
                isUpdatingOne: true,
                successUpdatingOne: undefined,
                failOnUpdatingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_UPDATE_SUCCESS: {
            const updatedList = state.list.some((item: ExternalEntityAssociationViewModel) => item.id === action.payload.id)
                ? state.list.map((item: ExternalEntityAssociationViewModel) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: true,
                failOnUpdatingOne: false,
                list: updatedList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_UPDATE_FAILURE:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: false,
                failOnUpdatingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_DELETE_REQUEST:
            return {
                ...state,
                isDeletingOne: true,
                successDeletingOne: undefined,
                failOnDeletingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_DELETE_SUCCESS:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: true,
                failOnDeletingOne: false,
                list: state.list.filter((item: ExternalEntityAssociationViewModel) => item.id !== action.id),
                error: undefined
            };
        case ActionEvents.PERSON_EXTERNAL_ASSOCIATION_DELETE_FAILURE:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: false,
                failOnDeletingOne: true,
                error: action.error
            };
        default:
            return state;
    }
};
